/**
 * VoiceDesk API — real HTTP calls to /api/voicedesk/* backend.
 * Uses XMLHttpRequest to bypass service worker fetch interception.
 */
const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('vd_token');
  const h = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

function request(method, path, body) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${API}/api/voicedesk${path}`, true);

    const headers = authHeaders();
    Object.keys(headers).forEach(k => xhr.setRequestHeader(k, headers[k]));

    xhr.onload = function () {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch { /* empty */ }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
        return;
      }

      // Auto-redirect on 401 for protected routes
      const isAuthEndpoint = path.startsWith('/auth/login') || path.startsWith('/auth/signup') || path.startsWith('/auth/forgot') || path.startsWith('/auth/reset');
      if (xhr.status === 401 && !isAuthEndpoint) {
        localStorage.removeItem('vd_token');
        window.location.href = '/voicedesk/login';
        reject(new Error('Session expired'));
        return;
      }

      // Extract error message
      let msg = `Request failed (${xhr.status})`;
      if (data && typeof data.detail === 'string') msg = data.detail;
      else if (data && Array.isArray(data.detail)) msg = data.detail.map(e => e?.msg || e?.message || JSON.stringify(e)).join('. ');
      reject(new Error(msg));
    };

    xhr.onerror = function () {
      reject(new Error('Network error. Please check your connection.'));
    };

    xhr.send(body ? JSON.stringify(body) : null);
  });
}

// ── Auth ──────────────────────────────────────────────────────────────
export const vdAuthApi = {
  login: (data) => request('POST', '/auth/login', data),
  signup: (data) => request('POST', '/auth/signup', data),
  me: () => request('GET', '/auth/me'),
  updateProfile: (data) => request('PUT', '/auth/profile', data),
  changePassword: (data) => request('POST', '/auth/change-password', data),
  forgotPassword: (email) => request('POST', '/auth/forgot-password', { email }),
  forgotPasswordEmailOTP: (email) => request('POST', '/auth/forgot-password/email-otp', { email }),
  forgotPasswordPhoneOTP: (phone) => request('POST', '/auth/forgot-password/phone-otp', { phone }),
  verifyOTP: (data) => request('POST', '/auth/verify-otp', data),
  resetPassword: (token, password) => request('POST', '/auth/reset-password', { token, password }),
};

// ── Company ──────────────────────────────────────────────────────────
export const vdCompanyApi = {
  get: () => request('GET', '/company'),
  update: (data) => request('PUT', '/company', data),
};

// ── Departments ──────────────────────────────────────────────────────
export const vdDepartmentApi = {
  list: () => request('GET', '/departments'),
  create: (data) => request('POST', '/departments', data),
  update: (id, data) => request('PUT', `/departments/${id}`, data),
  delete: (id) => request('DELETE', `/departments/${id}`),
};

// ── Directory ────────────────────────────────────────────────────────
export const vdDirectoryApi = {
  list: () => request('GET', '/directory'),
  create: (data) => request('POST', '/directory', data),
  update: (id, data) => request('PUT', `/directory/${id}`, data),
  delete: (id) => request('DELETE', `/directory/${id}`),
};

// ── Routing ──────────────────────────────────────────────────────────
export const vdRoutingApi = {
  list: () => request('GET', '/routing'),
  create: (data) => request('POST', '/routing', data),
  update: (id, data) => request('PUT', `/routing/${id}`, data),
  delete: (id) => request('DELETE', `/routing/${id}`),
};

// ── AI Config ────────────────────────────────────────────────────────
export const vdAIConfigApi = {
  get: () => request('GET', '/ai-config'),
  update: (data) => request('PUT', '/ai-config', data),
};

// ── Business Hours ───────────────────────────────────────────────────
export const vdHoursApi = {
  get: () => request('GET', '/hours'),
  update: (data) => request('PUT', '/hours', data),
};

// ── Onboarding ───────────────────────────────────────────────────────
export const vdOnboardingApi = {
  complete: () => request('POST', '/onboarding/complete'),
};

// ── Analytics ────────────────────────────────────────────────────────
export const vdAnalyticsApi = {
  summary: (range = '7d') => request('GET', `/analytics/summary?range=${range}`),
  volume: (range = '7d') => request('GET', `/analytics/volume?range=${range}`),
  departments: (range = '7d') => request('GET', `/analytics/departments?range=${range}`),
  peakHours: (range = '7d') => request('GET', `/analytics/peak-hours?range=${range}`),
  calls: (range = '7d', limit = 20, disposition = '') =>
    request('GET', `/analytics/calls?range=${range}&limit=${limit}${disposition ? `&disposition=${disposition}` : ''}`),
  live: () => request('GET', '/analytics/live'),
  seed: () => request('POST', '/analytics/seed'),
  streamUrl: () => {
    const token = localStorage.getItem('vd_token');
    return `${API}/api/voicedesk/analytics/stream?token=${encodeURIComponent(token)}`;
  },
};
